import { useCallback, useMemo } from 'react';
import { PermissionKey } from '../../apollo/graphqlSchema';
import { DevPermission } from '../../../redux/slices/permission';
import { useAppSelector } from '../../../redux/hooks';
import { assertNotNil } from '../../types';
import { useBootstrapSelector } from '../../../redux/selectorHooks';
import { IS_SCREENSHOT_MODE } from '../../../config/constants';
import { getPermissionKeyAndSchemaIdsFromRoles } from '../../../pages/PermissionControls/utils';

interface ICheckIsPermittedArg {
  requiredPermissionKeys: PermissionKey[];
  schemaId?: string;
  validateKind?: 'AND' | 'OR';
}

/**
 * This hook is used to check if the user has the permission to perform an action.
 */
const usePermission = () => {
  const userPermission = useAppSelector((state) => state.permission);
  const permissionConfig = useBootstrapSelector('permissionConfig');
  assertNotNil(permissionConfig);
  const { roles, devPermission } = userPermission;
  const allPermissionsFromUserRoles = useMemo(() => {
    return getPermissionKeyAndSchemaIdsFromRoles(roles);
  }, [roles]);

  const checkIsPermitted = useCallback(
    ({
      requiredPermissionKeys,
      schemaId,
      validateKind = 'AND',
    }: ICheckIsPermittedArg) => {
      // Special case in Embedded Mode: disable all permission controls.
      // if user hacks to type embedded mode url, we will need to rely on prevention of backend
      if (IS_SCREENSHOT_MODE) {
        return true;
      }

      // Special case: if permission control is disabled, all permissions are granted
      // Or permissionKeys is empty, permission is granted, this check is used in case of loop of permission check
      // but some of the check is not required permission to view/do something.
      if (
        !permissionConfig.enablePermissionControl ||
        requiredPermissionKeys.length === 0
      ) {
        return true;
      }

      // Special case: if dev permission is enabled, respect the permission on selected for QA purposes
      if (devPermission !== DevPermission.Default) {
        return devPermission === DevPermission.ForceEnable;
      }

      // check if passed in permissionKeys are all / any found in the user's permissions
      // and also match the schemas if provided
      const functionName = validateKind === 'AND' ? 'every' : 'some';

      return requiredPermissionKeys[functionName]((permissionKey) => {
        const foundPermission = allPermissionsFromUserRoles.find(
          (permission) => {
            if (schemaId && permission.permissionKey === permissionKey) {
              return (
                permission.schemaIds.includes(schemaId) ||
                permission.schemaIds.includes(permissionConfig.allSchema.id)
              );
            }
            return permission.permissionKey === permissionKey;
          },
        );
        return !!foundPermission;
      });
    },
    [allPermissionsFromUserRoles, devPermission, permissionConfig],
  );

  return { checkIsPermitted };
};

export type ICheckIsPermitted = ReturnType<
  typeof usePermission
>['checkIsPermitted'];

export default usePermission;
